import {
  EmptystateChartColored,
  InfoCircle,
  WarningTriangleColored,
} from "components/icons/generated";
import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import Card from "ds/components/Card";
import EmptyState from "ds/components/EmptyState";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";

import styles from "./styles.module.css";
import UsageWorkersMetricCards from "../MetricCards";
import ConcurrencyChart from "../ConcurrencyChart";
import {
  formatWorkerUsageDate,
  hasWorkerUsageContract,
  isWorkerUsageEmpty,
  isWorkerUsagePastCycle,
} from "../utils";
import { WorkerUsageData } from "../types";

const CHART_DISCLAIMER =
  "These values are only directional. The percentile is computed over the cycle so far and is finalized on a monthly basis. The data is covering solely the current account and isn't covering data from other accounts of the same organization.";

const TREND_DISCLAIMER =
  "We can't display your contracted limit right now, so this page is showing usage trend only.";

type UsageWorkersContentProps = {
  usage?: WorkerUsageData;
};

const UsageWorkersContent = ({ usage }: UsageWorkersContentProps) => {
  if (!usage) {
    return (
      <Box align="center" justify="center" grow="1">
        <EmptyState
          title="Couldn't load usage data"
          icon={WarningTriangleColored}
          caption="Something went wrong while loading worker usage."
        />
      </Box>
    );
  }

  const hasContract = hasWorkerUsageContract(usage);
  const showsBurstMinutes = hasContract && !usage.multiAccountContract;

  const multiAccountContractCallout = hasContract && usage.multiAccountContract && (
    <Callout variant="info" title="Workers are shared across multiple accounts">
      Your workers are part of a shared pool between multiple accounts. Please connect with your
      Customer Success Manager for overages calculations.
    </Callout>
  );

  if (isWorkerUsageEmpty(usage)) {
    return (
      <>
        {multiAccountContractCallout}

        <Box align="center" justify="center" grow="1">
          <EmptyState
            title="No private worker usage"
            icon={EmptystateChartColored}
            caption="This page counts private workers only. Runs on Spacelift's public workers are not included."
          />
        </Box>
      </>
    );
  }

  return (
    <>
      {multiAccountContractCallout}

      <Box direction="column" padding="x-large" gap="x-large" grow="1">
        <UsageWorkersMetricCards usage={usage} />

        <Card title="Worker usage" grow="1" contentClassName={styles.cardContent}>
          <ConcurrencyChart usage={usage} />

          <Box direction="row" align="start" gap="small" padding="medium xx-large 0 0">
            <Box shrink="0">
              <Icon src={InfoCircle} color="tertiary" />
            </Box>
            <Typography variant="p-body4" tag="p" color="secondary">
              {hasContract ? CHART_DISCLAIMER : TREND_DISCLAIMER}
            </Typography>
          </Box>

          {/* sr-only must sit on a wrapper: table height acts as a minimum, so the
              1px clip never applies to the table itself and it stretches the page. */}
          <div className="sr-only">
            <table>
              <caption>Daily worker usage for the billing cycle</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Maximum workers</th>
                  {hasContract && (
                    <th scope="col">
                      P{usage.contractedPercentile}
                      {!isWorkerUsagePastCycle(usage) && " to date"}
                    </th>
                  )}
                  {showsBurstMinutes && <th scope="col">Burst minutes used</th>}
                </tr>
              </thead>
              <tbody>
                {usage.daily.map((point) => (
                  <tr key={point.day.getTime()}>
                    <th scope="row">{formatWorkerUsageDate(point.day, "EEE d MMM yyyy")}</th>
                    <td>{point.max}</td>
                    {hasContract && <td>{point.percentileToDateValue ?? "—"}</td>}
                    {showsBurstMinutes && <td>{point.burstMinutesUsed ?? "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Box>
    </>
  );
};

export default UsageWorkersContent;
